import { ExampleDoubleComponent } from '../example-double/example-double.component';
import { ExampleMaster } from '../../models/example-master.model';
import { ExampleMasterCondition } from '../../models/example-master-condition.model';
import { DataAction } from '../../../enum/data-action.enum';
import { TableSelectionMode } from '../../../enum/table-selection-mode.enum';
import { ExampleSingleComponent } from '../example-single/example-single.component';
import { ExampleService } from '../../services/example.service';
import { ExampleData } from '../../models/example-data.model';
import { takeUntil, Subject, switchMap, map, of, Observable, lastValueFrom, firstValueFrom } from 'rxjs';
import { Option } from '../../../models/option.model';
import { Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { Component } from '@angular/core';
import { SimpleSearchComponent } from '../../../components/abstract/simple/simple-search.component';

@Component({
  selector: 'app-example-search',
  templateUrl: './example-search.component.html',
  styleUrls: ['./example-search.component.scss']
})
export class ExampleSearchComponent extends SimpleSearchComponent {

  // single or multiple
  override  tableSelectionMode: 'single' | 'multiple' = TableSelectionMode.SINGLE;

  // Component 的 class 名稱
  override componentClassName: string = 'ExampleSearchComponent';

  // Table 資料的 key 欄位名稱
  override rowDataKeyName: string = 'uuid';

  // Table 資料刪除時要取哪個欄位的值來顯示提示
  override rowDataDeleteHintFieldName: string = 'input';

  // 為了寫程式可以點出提示，override 這個自行定義資料型別
  override rowCurrentData: ExampleMaster | undefined;

  // 顯示單身批次刪除的按鈕
  override canBatchDeleteRows: boolean = true;

  // 下拉選單的 Options 資料
  exampleOptions: Option[] = [];

  // 動態下拉選單的 Options 資料
  dynamicOptions: Option[] = [];

  // 自動完成的 Suggestions 資料
  exampleDataSuggestions: ExampleData[] = [];
  // 自動完成欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private exampleDataSubject$ = new Subject<void>();

  // 自動完成 + 下拉的 Suggestions 資料
  allExampleData?: ExampleData[];
  // 自動完成 + 下拉的 Suggestions 資料
  allExampleDataSuggestions: ExampleData[] = [];
  // 自動完成 + 下拉欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private allExampleDataSubject$ = new Subject<void>();

  constructor(private exampleService: ExampleService) {
    super();
  }

  override initFormGroup(): void {
    this.formGroup = this.formBuilder.nonNullable.group({
      input: '',
      calendar: '',
      dropdown: ['', Validators.required],
      autoCompleteCode: '',
      inputNumber: null,
      calendarRange: '',
      custValidation: ['', this.custValidator()],
      dynamicDropdown: '',
      autoCompleteDropdownCode: ''
    });
  }

  override initFormControlDisabled(): void {
    return;
  }

  override initStaticOptions(): void {
    // 下拉選單選項
    this.exampleOptions = [
      {
        id: 'option.yes',
        label: this.translateService.instant('option.yes'),
        value: 'Y'
      },
      {
        id: 'option.no',
        label: this.translateService.instant('option.no'),
        value: 'N'
      }
    ];
  }

  override async initFormData(): Promise<any> {
    this.formGroup.patchValue({
      dropdown: 'Y'
    });
    await this.setDynamicOptions();
  }

  /**
   * 建議這些要在程式一開始前就取回來的東西分不同 function 處理
   */
  async setDynamicOptions(): Promise<void> {
    console.log('setDynamicOptions start');
    this.dynamicOptions = await lastValueFrom(
      this.exampleService.queryAllExampleDataOptions().pipe(takeUntil(this._destroying$))
    );
    console.log('setDynamicOptions data = ' + JSON.stringify(this.dynamicOptions));
    console.log('setDynamicOptions end');
  }

  override afterFormDataLoadSuccess(): void {
    return;
  }

  override afterFormDataLoadFail(): void {
    return;
  }

  override initExtraDetailMenu(): void {
    this.detailMenu.push(
      {
        id: this.parseToId('btnDetailMenuSomeAction'),
        label: '選擇後的動作',
        automationId: '選擇後的動作',
        command: () => {
          this.someAction();
        }
      }
    );
  }

  override setExtraDetailMenuStatus(): void {
    console.log('setExtraDetailMenuStatus');
    let detailMenuButtonOther = this.detailMenu.find(menuItem => menuItem.id == this.parseToId('btnDetailMenuSomeAction'));
    if (detailMenuButtonOther) {
      detailMenuButtonOther.disabled = !this.rowSelectedDatas || this.rowSelectedDatas.length <= 0;
    }
  }

  override afterLangChange(): void {
    this.languageService.processOptionTranslation(this.exampleOptions);
  }

  override initTableColumns(): void {
    this.tableColumns = [
      {
        header: '文字輸入',
        field: 'input',
        sortable: true
      },
      {
        header: '選擇日期',
        field: 'calendar',
        sortable: true
      },
      {
        header: '下拉選單',
        field: 'dropdown',
        translateArrayValue: {
          datas: this.exampleOptions,
          valueField: 'value',
          displayField: 'label'
        },
        sortable: true
      },
      {
        header: '自動完成',
        field: 'autoCompleteName',
        translateDataValueField: 'autoCompleteName',
        width: 180,
        sortable: true
      },
      {
        header: '數字輸入',
        field: 'inputNumber',
        numberField: true,
        sortable: true
      },
      {
        header: '動態下拉',
        field: 'dynamicDropdown',
        translateArrayValue: {
          datas: this.dynamicOptions,
          valueField: 'value',
          displayField: 'label'
        },
        width: 180,
        sortable: true
      },
      {
        header: '自訂欄位驗證',
        field: 'custValidation',
        width: 350,
        overflowBreak: true
      },
      {
        header: '自動完成+下拉',
        field: 'autoCompleteDropdownName',
        translateDataValueField: 'autoCompleteDropdownName',
        width: 180,
        sortable: true
      }
    ];
  }

  override initExtraRowActionMenu(): void {
    this.rowActionMenu.push(
      {
        id: this.parseToId('btnRowActionMenuSomeRowAction'),
        label: '額外的動作',
        automationId: '額外的動作',
        icon: 'pi pi-fw pi-megaphone',
        command: () => {
          this.someRowAction();
        }
      }
    );
  }

  override setExtraRowActionMenuStatus(): void {
    this.setRowActionItemDisable('btnRowActionMenuSomeRowAction', (this.rowCurrentData?.inputNumber ?? 0) > 120);
  }

  override initDialogConfig(dataAction: DataAction) {
    console.log('initDialogConfig dataAction = ' + dataAction);
    let dialogHeader: string = '';
    let dialogData: any = {};

    if (dataAction == DataAction.NEW) {
      dialogHeader = '功能標題新增資料';
    } else if (dataAction == DataAction.VIEW) {
      dialogHeader = '功能標題檢視資料';
      dialogData = this.rowCurrentData;
    }

    this.dialogConfig = {
      component: ExampleSingleComponent,
      dataAction: dataAction,
      header: dialogHeader,
      data: dialogData
    };

    // 這裡只是為了透過同一個畫面來做雙檔的範例，實際上不會有這樣
    if (this.activatedRoute.snapshot.url.toString() == 'search-double') {
      this.dialogConfig.component = ExampleDoubleComponent;
    }

    console.log('initDialogConfig dialogConfig = ' + JSON.stringify(this.dialogConfig));
    console.log('initDialogConfig dialogConfig component = ' + this.dialogConfig.component.name);
  }

  override doExtraDetailDialogClose(returnedData: any): void {
    this.systemMessageService.info('Detail 關閉後要做什麼');
  }

  override async doRowActionDelete(): Promise<boolean> {
    return new Promise(resolve => {
      // 模擬刪除處理需要時間
      setTimeout(() => {
        // 這只是範例，實際上還是要自己視狀況控制什麼時候需要重新查詢資料
        // 例如成功刪除資料後
        this.requery();
        resolve(true);
      }, this.exampleService.delay);
    });
  }

  override afterSelectedRowDataCountChange(selectedDataCount: number): void{
    // 控制當有選取資料的時候按鈕才能按
    let menuItem = this.detailMenu.find(menuItem => menuItem.id == this.parseToId('btnDetailMenuSomeAction'));
    if (menuItem) {
      menuItem.disabled = selectedDataCount <= 0;
    }
    this.redrawDetailMenu();
  }

  override afterRowDataCountChange(rowDataCount: number): void {
    return;
  }

  override afterDetailTabActiveItemChange(): void {
    return;
  }

  override async beforeQuery(): Promise<boolean> {
    return true;
  }

  override async doQuery(requeryCondition?: any): Promise<boolean> {
    console.log('ExampleSearchComponent doQuery');
    // 用 getRawValue 取值
    // console.log(JSON.stringify(this.formGroup.getRawValue()));

    let exampleMasterCondition: ExampleMasterCondition = new ExampleMasterCondition();
    if (requeryCondition) {
      exampleMasterCondition = requeryCondition;
    } else {
      exampleMasterCondition.input = this.formControl('input').getRawValue();
      exampleMasterCondition.calendar = this.formControl('calendar').getRawValue();
      exampleMasterCondition.dropdown = this.formControl('dropdown').getRawValue();
      exampleMasterCondition.autoCompleteCode =
        !!this.formControl('autoCompleteCode').getRawValue() ? this.formControl('autoCompleteCode').getRawValue().exampleDataCode : '';
      exampleMasterCondition.inputNumber = this.formControl('inputNumber').getRawValue();
      exampleMasterCondition.custValidation = this.formControl('custValidation').getRawValue();
      exampleMasterCondition.dynamicDropdown = this.formControl('dynamicDropdown').getRawValue();
      exampleMasterCondition.autoCompleteDropdownCode =
        !!this.formControl('autoCompleteDropdownCode').getRawValue() ? this.formControl('autoCompleteDropdownCode').getRawValue().exampleDataCode : '';
    }
    this.requeryCondition = exampleMasterCondition;
    console.log('doQuery exampleMasterCondition = ' + JSON.stringify(exampleMasterCondition));

    try {
      const data = await firstValueFrom(
        this.exampleService.queryExampleMasterDatas(exampleMasterCondition).pipe(
          takeUntil(this._destroying$)
        )
      );

      this.detailDatas = data ? data : [];

      // 正確查詢完後回傳 true
      return true;
    } catch (error) {
      console.error('Error: ', error);
      return false;
    }
  }

  custValidator(): ValidatorFn {
    return (formControl: AbstractControl): ValidationErrors | null => {
      if (!formControl.parent) {
        return null;
      }

      // 欄位要做什麼驗證
      const error = formControl.parent?.get('custValidation')?.value == '1';

      // 如果有錯
      if (error) {
        // 回傳 ValidationErrors，duplicate 是 error 的 key
        return { duplicate: true };
      }
      // 驗證結果符合需求，回傳 null
      return null;
    }
  }

  searchExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.exampleDataSubject$.observed) {
      this.exampleDataSubject$.pipe(
        switchMap(keyword => this.exampleService.queryExampleDatas(keyword!)),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.exampleDataSuggestions = data;
        console.log('this.exampleDataSuggestions = ' + this.exampleDataSuggestions.length);
      });
    }

    this.exampleDataSubject$.next(event.query);
  }

  searchAllExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.allExampleDataSubject$.observed) {
      this.allExampleDataSubject$.pipe(
        switchMap(keyword => {
          // 如果 allExampleData 已經有取過值了就用它，不然就呼叫 API 取
          let data: Observable<ExampleData[]> = this.allExampleData ? of(this.allExampleData) : this.exampleService.queryAllExampleDatas()
          return data.pipe(
            map(data => {
              // console.log('searchAllExampleDataSuggestions = ' + keyword);
              // 把回傳所有的值塞到 allExampleData
              this.allExampleData = data;
              // 根據輸入的關鍵字過濾
              return data.filter(exampleData => exampleData.exampleDataName.indexOf(keyword!) >= 0)
            })
          );
        }),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.allExampleDataSuggestions = data;
      });
    }

    this.allExampleDataSubject$.next(event.query);
  }

  someAction(): void {
    // 這裡是為了寫單選和複選的使用例子，實際上不會有程式一下是單選一下是複選?
    if (this.tableSelectionMode == TableSelectionMode.SINGLE) {
      this.systemMessageService.info(this.rowSelectedDatas.input);
    } else {
      this.systemMessageService.info(this.rowSelectedDatas.map((data: ExampleMaster) => data.input).toString());
    }

    // 很重要，自己決定要不要把記錄下來選擇的資料清空
    this.clearRowSelectedDatas();
  }

  someRowAction(): void {
    this.systemMessageService.info(this.rowCurrentData?.input ?? '');
  }
}
